import React from "react";
import { Transaction } from "../../../services/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faDownload,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import { format } from "date-fns";
import { saveAs } from "file-saver";

interface TransactionModalProps {
  transaction: Transaction | null;
  isOpen: boolean;
  onClose: () => void;
}

const TransactionModal: React.FC<TransactionModalProps> = ({
  transaction,
  isOpen,
  onClose,
}) => {
  if (!isOpen || !transaction) return null;

  const downloadTransactionDetails = () => {
    const transactionData = {
      "Transaction ID": transaction._id,
      "User ID": transaction.user_id,
      "Agent ID": transaction.agent_id,
      "Restaurant ID": transaction.Restaurant_id,
      Amount: `₦${transaction.amount}`,
      Status: transaction.status,
      Type: transaction.type,
      Description: transaction.description,
      "Transaction Code": transaction.transaction_code,
      "Registration Bonus Used": transaction.registration_bonus_used,
      "Referral Bonus Used": transaction.referral_bonus_used,
      "Created At": format(new Date(transaction.created_at), "PPpp"),
      "Updated At": format(new Date(transaction.updated_at), "PPpp"),
    };

    const csvContent = [
      Object.keys(transactionData).join(","),
      Object.values(transactionData).join(","),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, `transaction_${transaction._id}.csv`);
  };

  const statusBadge = (status: string) => {
    const statusClass =
      {
        successful: "bg-green-100 text-green-800 border-green-200",
        unsuccessful: "bg-red-100 text-red-800 border-red-200",
        pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
      }[status.toLowerCase()] || "bg-gray-100 text-gray-800 border-gray-200";

    return (
      <span
        className={`px-3 py-1 rounded-full text-sm font-medium border ${statusClass}`}
      >
        {status}
      </span>
    );
  };

  const typeBadge = (type: string) => {
    const typeClass =
      {
        debit: "bg-red-100 text-red-800 border-red-200",
        credit: "bg-green-100 text-green-800 border-green-200",
      }[type.toLowerCase()] || "bg-gray-100 text-gray-800 border-gray-200";

    return (
      <span
        className={`px-3 py-1 rounded-full text-sm font-medium border ${typeClass}`}
      >
        {type}
      </span>
    );
  };

  const renderField = (
    label: string,
    value: any,
    isMonospace = false,
    isCurrency = false
  ) => {
    const displayValue = value || "Not available";
    const valueClass = isMonospace
      ? "text-sm text-gray-800 font-mono bg-gray-50 p-3 rounded-lg border border-gray-200 mt-1 break-all"
      : "text-sm text-gray-800 bg-gray-50 p-3 rounded-lg border border-gray-200 mt-1";

    return (
      <div>
        <label className="text-sm font-medium text-gray-600 flex items-center gap-1">
          {label}
          {!value && (
            <FontAwesomeIcon
              icon={faInfoCircle}
              className="w-3 h-3 text-gray-400"
            />
          )}
        </label>
        <p className={valueClass}>
          {isCurrency && value ? `₦${value}` : displayValue}
        </p>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto border border-gray-200">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 rounded-t-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
                <div className="w-2 h-8 bg-gradient-to-b from-amber-400 to-amber-600 rounded-full"></div>
                Transaction Details
              </h2>
              <p className="text-sm text-gray-500 mt-2 font-mono">
                ID: {transaction._id}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={downloadTransactionDetails}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-lg hover:from-amber-600 hover:to-amber-700 transition-all duration-200 shadow-md hover:shadow-lg text-sm font-medium"
              >
                <FontAwesomeIcon icon={faDownload} className="w-3 h-3" />
                Export
              </button>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Amount Highlight */}
          <div className="mb-8 p-6 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl border border-amber-200">
            <div className="text-center">
              <p className="text-sm font-medium text-amber-700 mb-2">
                Transaction Amount
              </p>
              <p className="text-3xl font-bold text-amber-800">
                ₦{transaction.amount}
              </p>
              <div className="flex items-center justify-center gap-4 mt-3">
                {statusBadge(transaction.status)}
                {typeBadge(transaction.type)}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Basic Information */}
            <div className="space-y-6">
              <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <div className="w-1 h-6 bg-amber-500 rounded-full"></div>
                  Basic Information
                </h3>

                <div className="space-y-4">
                  {renderField("Transaction ID", transaction._id, true)}
                  {renderField(
                    "Transaction Code",
                    transaction.transaction_code,
                    true
                  )}
                  {renderField("Description", transaction.description)}
                </div>
              </div>
            </div>

            {/* Additional Details */}
            <div className="space-y-6">
              <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <div className="w-1 h-6 bg-blue-500 rounded-full"></div>
                  Related IDs
                </h3>

                <div className="space-y-4">
                  {renderField("User ID", transaction.user_id, true)}
                  {renderField("Agent ID", transaction.agent_id, true)}
                  {renderField(
                    "Restaurant ID",
                    transaction.Restaurant_id,
                    true
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Bonus Information */}
          <div className="mt-8">
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <div className="w-1 h-6 bg-green-500 rounded-full"></div>
                Bonus Information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-lg border border-green-200">
                  <label className="text-sm font-medium text-green-700 mb-2 block">
                    Registration Bonus Used
                  </label>
                  <p className="text-lg font-semibold text-green-800">
                    ₦{transaction.registration_bonus_used || 0}
                  </p>
                </div>

                <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                  <label className="text-sm font-medium text-blue-700 mb-2 block">
                    Referral Bonus Used
                  </label>
                  <p className="text-lg font-semibold text-blue-800">
                    ₦{transaction.referral_bonus_used || 0}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Timestamps */}
          <div className="mt-8">
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <div className="w-1 h-6 bg-purple-500 rounded-full"></div>
                Timestamps
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-lg border border-purple-200">
                  <label className="text-sm font-medium text-purple-700 mb-2 block">
                    Created At
                  </label>
                  <p className="text-sm text-purple-800 font-medium">
                    {format(new Date(transaction.created_at), "PPpp")}
                  </p>
                </div>

                <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-4 rounded-lg border border-indigo-200">
                  <label className="text-sm font-medium text-indigo-700 mb-2 block">
                    Updated At
                  </label>
                  <p className="text-sm text-indigo-800 font-medium">
                    {format(new Date(transaction.updated_at), "PPpp")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 rounded-b-xl p-6">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransactionModal;
